import { Link } from "react-router-dom";
import { Cube, ChartLineUp, Certificate, MapPin, Calendar, ArrowRight, Coins } from "@phosphor-icons/react";
import { Section, PageHero, EyebrowLabel, CalloutCard, BulletList } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

const CASES = [
  {
    slug: "iron-ore-mid-cap-jorc",
    commodity: "Iron Ore · Fe",
    icon: Cube,
    region: "Odisha, India",
    duration: "8 weeks",
    title: "From four CSVs to a JORC-aligned resource in eight weeks.",
    summary:
      "A mid-cap iron ore developer replaced a three-vendor stack — CSV wrangler, desktop kriging suite, spreadsheet economics — with a single SRES tenant. Their next resource statement shipped on schedule with a full reproducibility snapshot.",
    challenge: [
      "Three separate tools each owning one slice of the workflow.",
      "No unified audit trail — every re-run required a fresh chain of custody.",
      "Board-ready CPR PDFs assembled manually in Word from a dozen source files.",
    ],
    solution: [
      "Ingested 112 drillholes (Collar / Survey / Assay / Geology) into SRES with zero manual column mapping.",
      "Built the Fe block model with Ordinary Kriging plus per-lithology density mapping.",
      "Generated the CPR PDF, DOCX, cut-off sensitivity report and continuous drill-log bundle in a single day.",
    ],
    outcome: [
      { metric: "3 weeks", label: "faster to board than the previous cycle" },
      { metric: "0", label: "manual CSV hand-offs" },
      { metric: "100%", label: "reproducibility of every signed run" },
    ],
  },
  {
    slug: "gold-junior-exploration-cutoff",
    commodity: "Gold · Au",
    icon: ChartLineUp,
    region: "Karnataka, India",
    duration: "6 weeks",
    title: "A gold junior stress-tests economics before committing capital.",
    summary:
      "A junior explorer used SRES's Monte-Carlo NPV and cut-off sweep tools to defend a phase-two drilling budget to their board — with a transparent P10/P50/P90 picture rather than a single point estimate.",
    challenge: [
      "Board wanted a defensible NPV range, not one number.",
      "Existing spreadsheets could not communicate risk cleanly.",
      "No way to compare cut-off scenarios apples-to-apples.",
    ],
    solution: [
      "Ran SRES's cut-off × NPV sweep across four commodity price scenarios.",
      "Overlaid a 5,000-trial Monte-Carlo NPV simulation with triangular distributions.",
      "Assembled a sensitivity DOCX pack that the CFO presented directly to the board.",
    ],
    outcome: [
      { metric: "P10/P50/P90", label: "disclosed alongside deterministic NPV" },
      { metric: "4×", label: "faster board turnaround" },
      { metric: "$18 M", label: "phase-two budget approved" },
    ],
  },
  {
    slug: "consultancy-standardises-cpr",
    commodity: "Consultancy · Multi-commodity",
    icon: Certificate,
    region: "Bengaluru + APAC",
    duration: "12 weeks",
    title: "A mining consultancy standardises CPRs across six client accounts.",
    summary:
      "A resource-consulting firm rolled SRES out across six enterprise clients, replacing bespoke CPR templates with a single, standards-native assembly — dramatically cutting review-cycle time for their Competent Persons.",
    challenge: [
      "Every client had a slightly different CPR template — none audit-friendly.",
      "Competent Persons spent weeks on formatting, not on judgement.",
      "Public share links for regulators were emailed as one-off PDF attachments.",
    ],
    solution: [
      "Deployed SRES tenants for each of the six clients with company-scoped admins.",
      "Ported client-specific narrative styles into the SRES CPR templates once.",
      "Introduced watermarked public share links with expiry and audit logs.",
    ],
    outcome: [
      { metric: "3×", label: "reduction in CPR assembly time" },
      { metric: "1", label: "unified template across all six accounts" },
      { metric: "100%", label: "share-link auditability" },
    ],
  },
];

export default function CaseStudiesPage() {
  useSEO({
    title: "Case Studies · Real teams, real resource statements",
    description: "How mid-cap developers, junior explorers and consultancies deploy SRES to deliver defensible resource statements faster.",
    keywords: "SRES case studies, mining resource estimation, JORC case study, NI 43-101 case study",
    path: "/case-studies",
  });

  return (
    <div data-testid="case-studies-page">
      <PageHero
        eyebrow="Case studies"
        title="Real teams. Real drillholes. Real Competent Person Reports."
        description="Three anonymised case studies showing how SRES gets deployed on live projects across the resource lifecycle."
        testid="cases-hero"
      />

      <Section className="py-20 md:py-24" testid="cases-list">
        <div className="space-y-14">
          {CASES.map((c, i) => (
            <article
              key={c.slug}
              className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-8 lg:gap-12 p-8 md:p-10 rounded-2xl border border-slate-200 bg-white"
              data-testid={`case-${c.slug}`}
            >
              <div className="flex lg:flex-col gap-6 lg:gap-4 items-start">
                <div className="w-14 h-14 rounded-xl bg-brand-blue text-white flex items-center justify-center shrink-0">
                  <c.icon size={22} weight="bold" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-brand-blue font-mono">{c.commodity}</div>
                  <div className="mt-3 text-[13px] text-slate-600 flex items-center gap-1"><MapPin size={12} /> {c.region}</div>
                  <div className="mt-1 text-[13px] text-slate-600 flex items-center gap-1"><Calendar size={12} /> {c.duration}</div>
                </div>
              </div>
              <div>
                <h2 className="font-heading font-black text-slate-900 text-2xl md:text-3xl leading-tight tracking-tight">{c.title}</h2>
                <p className="mt-4 text-slate-700 text-[15px] leading-relaxed">{c.summary}</p>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-3">The challenge</div>
                    <BulletList items={c.challenge} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-3">The SRES rollout</div>
                    <BulletList items={c.solution} />
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-3 gap-3">
                  {c.outcome.map((o) => (
                    <div key={o.label} className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="font-heading font-black text-brand-blue text-2xl leading-none tracking-tight">{o.metric}</div>
                      <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-slate-500">{o.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Section className="py-16 md:py-24 bg-[#F7F8FB]" testid="cases-cta">
        <CalloutCard
          eyebrow="Want to be the next case study?"
          title="Bring your project. We will scope a working pilot and publish the results together."
          description="Every pilot begins with a 45-minute working session on your own drillhole data. If we build a defensible model, you get a two-week trial workspace."
          cta="Scope a pilot"
          to="/contact"
          testid="cases-cta-callout"
        />
      </Section>
    </div>
  );
}
